import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Grape, Sun, Heart, Award, Coffee } from 'lucide-react';
import { aboutImage } from '@/lib/images';

const AboutSection = () => {
  const { language } = useLanguage();

  const content = {
    PL: {
      title: 'O nas',
      subtitle: 'Jesteśmy firmą rodzinną',
      description: `Oferujemy tylko tradycyjne produkty najwyższej jakości z włoskiej Apulii.\n\nW naszej ofercie znajdziesz:`,
      features: [
        { icon: Grape, title: 'Wina', desc: 'Naturalne, doskonałe wina prosto z winnicy Tenuta Viglione' },
        { icon: Sun, title: 'Oliwy', desc: 'Doskonałe w smaku i zdrowe oliwy Galantino, prosto z rodzinnego gaju oliwnego' },
        { icon: Heart, title: 'Przekąski', desc: 'Tradycyjne włoskie przekąski, bezpośrednio z piekarni Fiore di Puglia' },
        { icon: Award, title: 'Bezglutenowe', desc: 'Linię pieczyw i przekąsek bezglutenowych' },
        { icon: Coffee, title: 'Kawa', desc: 'Mocną włoską kawę. Sam wybierasz jaką markową włoska kawę dziś wypijesz!' },
      ],
    },
    EN: {
      title: 'About Us',
      subtitle: 'We are a family business',
      description: `We offer only traditional top-quality products from the Italian region of Apulia.\n\nIn our offer you will find:`,
      features: [
        { icon: Grape, title: 'Wines', desc: 'Natural, excellent wines straight from the Tenuta Viglione vineyard' },
        { icon: Sun, title: 'Olive Oils', desc: 'Delicious and healthy Galantino olive oils, straight from a family olive grove' },
        { icon: Heart, title: 'Snacks', desc: 'Traditional Italian snacks, directly from the Fiore di Puglia bakery' },
        { icon: Award, title: 'Gluten-free', desc: 'A line of gluten-free breads and snacks' },
        { icon: Coffee, title: 'Coffee', desc: 'Strong Italian coffee. You choose which branded Italian coffee you will drink today!' },
      ],
    },
  };

  const c = content[language];

  return (
    <section id="about" className="bg-secondary py-20">
      <div className="container mx-auto px-4">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative w-full"
          >
            <div className="aspect-[16/10] w-full overflow-hidden rounded-2xl bg-muted shadow-xl">
              <img
                src={aboutImage}
                alt="Vineyard cellar"
                loading="lazy"
                width="1600"
                height="1000"
                className="h-full w-full object-cover"
              />
            </div>

          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col justify-center"
          >
            <h2 className="mb-4 font-display text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
              {c.title}
            </h2>
            <p className="mb-6 font-serif text-xl text-primary italic">
              {c.subtitle}
            </p>
            <p className="mb-8 whitespace-pre-line text-muted-foreground leading-relaxed">
              {c.description}
            </p>

            {/* Features grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {c.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`rounded-xl bg-card p-4 transition-shadow hover:shadow-md ${
                    index === 4 ? 'sm:col-span-2 sm:flex sm:items-center sm:gap-6' : ''
                  }`}
                >
                  <feature.icon className={`text-primary ${index === 4 ? 'mb-0 h-10 w-10 flex-shrink-0' : 'mb-3 h-8 w-8'}`} />
                  <div>
                    <h3 className="mb-1 font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
